import { ReactNode, RefObject, useEffect, useMemo, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import "./ScrollFloat.css";

gsap.registerPlugin(ScrollTrigger);

interface ScrollFloatProps {
  children: ReactNode;
  className?: string;
  scrollContainerRef?: RefObject<HTMLElement>;
  triggerRef?: RefObject<HTMLElement>;
  containerClassName?: string;
  textClassName?: string;
  animationDuration?: number;
  ease?: string;
  scrollStart?: string;
  scrollEnd?: string;
  stagger?: number;
  scrub?: boolean | number;
  /** Per-character split looks sharper but breaks on resize; words are stable. */
  splitChars?: boolean;
}

const ScrollFloat = ({
  children,
  className = "",
  scrollContainerRef,
  triggerRef,
  containerClassName = "",
  textClassName = "",
  animationDuration = 1,
  ease = "back.inOut(2)",
  scrollStart = "center bottom+=50%",
  scrollEnd = "bottom bottom-=40%",
  stagger = 0.03,
  scrub = 1,
  splitChars = false,
}: ScrollFloatProps) => {
  const containerRef = useRef<HTMLHeadingElement>(null);

  const splitText = useMemo(() => {
    const text = typeof children === "string" ? children : "";
    const words = text.split(/\s+/).filter(Boolean);

    if (!splitChars) {
      return words.map((word, wordIndex) => (
        <span className="word" key={wordIndex}>
          {word}
        </span>
      ));
    }

    return words.map((word, wordIndex) => (
      <span className="word" key={wordIndex}>
        {word.split("").map((char, charIndex) => (
          <span className="char" key={charIndex}>
            {char}
          </span>
        ))}
      </span>
    ));
  }, [children, splitChars]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const targetSelector = splitChars ? ".char" : ".word";
    const targets = el.querySelectorAll(targetSelector);
    if (!targets.length) return;

    const triggerElement =
      triggerRef?.current ?? el.closest("section") ?? el;

    const scrollTriggerConfig = {
      trigger: triggerElement,
      start: scrollStart,
      end: scrollEnd,
      scrub,
      // Reserve compositor layers only while the trigger is active, not forever.
      onToggle: (self: ScrollTrigger) => {
        gsap.set(targets, {
          willChange: self.isActive ? "opacity, transform" : "auto",
        });
      },
      ...(scrollContainerRef?.current
        ? { scroller: scrollContainerRef.current }
        : {}),
    };

    const tween = gsap.fromTo(
      targets,
      {
        opacity: 0,
        yPercent: 55,
        scaleY: 1.35,
        scaleX: 0.92,
        transformOrigin: "50% 100%",
      },
      {
        duration: animationDuration,
        ease,
        opacity: 1,
        yPercent: 0,
        scaleY: 1,
        scaleX: 1,
        stagger,
        scrollTrigger: scrollTriggerConfig,
      }
    );

    let resizeTimer: number | undefined;
    const handleResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => ScrollTrigger.refresh(), 200);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.clearTimeout(resizeTimer);
      window.removeEventListener("resize", handleResize);
      tween.scrollTrigger?.kill();
      tween.kill();
      gsap.set(targets, { clearProps: "willChange" });
    };
  }, [
    animationDuration,
    ease,
    scrollContainerRef,
    triggerRef,
    scrollEnd,
    scrollStart,
    splitChars,
    stagger,
    scrub,
  ]);

  return (
    <h2
      ref={containerRef}
      className={`scroll-float ${containerClassName} ${className}`.trim()}
    >
      <span className={`scroll-float-text ${textClassName}`}>{splitText}</span>
    </h2>
  );
};

export default ScrollFloat;
